import { type ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api/client';
import { claimUserScopedStorage, sendBeforeSignOut, signOutUserScopedStorage } from '@/lib/storage/user-scoped';
import { useAuthStore } from './auth-store';
import { clearSignInAttempt } from './return-to';
import { clearLogoutPending, isLogoutPending, markLogoutPending, markSsoSupersedes, newSsoNonce, ssoReturnState, warnLogoutStillPending } from './logout-pending';
import { OidcRedirectStrategy, type AuthStrategy, settlePendingLogout, startOidcLogin, strategyForMode } from './strategy';
import { setAccessToken } from './token-store';
import type { AuthAudience, AuthMode, AuthStatus, Me } from './types';

interface AuthContextValue {
  status: AuthStatus;
  user: Me | null;
  /** null until boot has read `/auth/mode`. */
  mode: AuthMode | null;
  /** Start SSO — always OIDC, even when platform mode is LOCAL. */
  loginWithSso: (audience?: AuthAudience) => void;
  /** Begin the active strategy's login (OIDC redirect, or a no-op in LOCAL). */
  login: (audience?: AuthAudience) => void;
  /** After a successful password (or MFA) login — store token and load `/me`. */
  acceptSession: (accessToken: string) => Promise<void>;
  /**
   * Sign out. Unsent answers are sent first (NW1); when they cannot all be
   * sent within a few seconds, nothing is signed out and `signOutHeld` turns
   * true, for the unsaved-answers dialog. `force` signs out without sending.
   * A call while a sign-out is under way joins it (NI-A): one `/auth/logout`.
   */
  logout: (opts?: { force?: boolean }) => Promise<void>;
  /** A sign-out is waiting for the person: their answers could not be sent (NW1). */
  signOutHeld: boolean;
  /** "Stay on this page": the held sign-out is cancelled; the answers keep saving. */
  cancelSignOut: () => void;
  /** Sign-out is sending the unsent answers (up to a few seconds): show it, and refuse a second click (NI-F). */
  signOutSending: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Start an SSO sign-in (D1). A sign-out the server never confirmed is ended
 * first; when it still cannot be (offline, server error) the sign-in goes
 * ahead anyway: an explicit sign-in is never locked out, and a completed IdP
 * round trip replaces the old cookie. The mark stays, recording the round
 * trip's nonce: only the callback of a completed login carries it back, so an
 * abandoned or refused round trip restores nobody.
 */
function ssoSignIn(audience: AuthAudience): void {
  if (!isLogoutPending()) return startOidcLogin(audience);
  void settlePendingLogout().then((settled) => {
    if (settled) return startOidcLogin(audience);
    warnLogoutStillPending('sso');
    const nonce = newSsoNonce();
    markSsoSupersedes(nonce);
    startOidcLogin(audience, nonce);
  });
}

/** Drop the SSO nonce from the address bar once read (it is single-use anyway). */
function dropSsoFragment(): void {
  try {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  } catch {
    // An address bar that keeps the fragment holds a spent nonce: harmless.
  }
}

/**
 * Boots the session once: pick the login strategy from the platform mode, run
 * silent auth (refresh cookie → in-memory access token), then load `/me`. Until
 * that resolves, `status` is `unknown` so the app shows a splash, not a flash of
 * the login screen.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const mode = useAuthStore((s) => s.mode);
  const set = useAuthStore((s) => s.set);
  const strategyRef = useRef<AuthStrategy>(new OidcRedirectStrategy());
  const [signOutHeld, setSignOutHeld] = useState(false);
  const [signOutSending, setSignOutSending] = useState(false);
  /**
   * The sign-out in progress (NI-A). Every further call, from either path,
   * joins it rather than starting another, from the first click until the
   * navigation, so `/auth/logout` is posted once. Cleared only when the
   * sign-out is held for the dialog, so "Sign out anyway" can start one.
   */
  const inFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let resolvedMode: AuthMode = 'LOCAL';
      try {
        const { mode: raw } = await api.get<{ mode: string }>('/auth/mode');
        resolvedMode = raw === 'OIDC' ? 'OIDC' : 'LOCAL';
        strategyRef.current = strategyForMode(resolvedMode);
      } catch {
        /* default LOCAL so the password form stays the primary path when the API is down */
        strategyRef.current = strategyForMode('LOCAL');
      }
      if (cancelled) return;
      set({ mode: resolvedMode });

      // D1: a sign-out that never reached the server (offline "Sign out
      // anyway") is finished before the refresh cookie is used. While it still
      // cannot be, the person stays signed out; the next attempt retries it.
      // Except on the return of a COMPLETED SSO sign-in started over it (its
      // nonce is in the fragment, which the API adds only after the login
      // replaced the cookie): ending "the old session" now would end the new
      // one. Any other landing on the callback keeps the mark and restores
      // nobody until the logout goes through.
      const ssoReturn = window.location.pathname === '/auth/callback' ? ssoReturnState(window.location.hash) : 'none';
      if (window.location.pathname === '/auth/callback' && window.location.hash.includes('sso=')) dropSsoFragment();
      if (ssoReturn === 'completed') {
        warnLogoutStillPending('sso-return');
        clearLogoutPending();
      } else if (ssoReturn === 'late') {
        // I3: this round trip's own nonce, but later than the window. The
        // cookie is the one the callback just set: settling the mark would
        // revoke it. Fail safe instead: restore nobody now, keep the mark.
        warnLogoutStillPending('sso-return-late');
        set({ status: 'unauthenticated', user: null });
        return;
      } else {
        const settled = await settlePendingLogout();
        if (cancelled) return;
        if (!settled) {
          set({ status: 'unauthenticated', user: null });
          return;
        }
      }

      const ok = await api.refreshSession();
      if (cancelled) return;
      if (!ok) {
        set({ status: 'unauthenticated', user: null });
        return;
      }
      try {
        const me = await api.get<Me>('/me');
        if (cancelled) return;
        // Browser data left by someone else at this browser goes before this person sees anything.
        claimUserScopedStorage(me.accountId);
        set({ status: 'authenticated', user: me });
      } catch {
        if (!cancelled) set({ status: 'unauthenticated', user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [set]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      mode,
      loginWithSso: (audience = 'STAFF') => ssoSignIn(audience),
      login: (audience) => (strategyRef.current.kind === 'oidc' ? ssoSignIn(audience ?? 'APPLICANT') : strategyRef.current.login(audience)),
      acceptSession: async (accessToken) => {
        setAccessToken(accessToken);
        const me = await api.get<Me>('/me');
        // D1: this sign-in replaced the cookie of a sign-out that never reached the server.
        clearLogoutPending();
        claimUserScopedStorage(me.accountId);
        set({ status: 'authenticated', user: me });
        clearSignInAttempt();
      },
      signOutHeld,
      cancelSignOut: () => setSignOutHeld(false),
      signOutSending,
      logout: ({ force = false } = {}) => {
        if (inFlightRef.current) return inFlightRef.current;
        const run = (async (): Promise<'held' | 'done'> => {
          // NW1 (user decision 2026-10-23): send what the server does not hold
          // yet while the session is still valid, for a few seconds at most.
          // Every sign-out goes through here, whichever button started it.
          if (!force) {
            setSignOutSending(true);
            let sent: boolean;
            try {
              sent = await sendBeforeSignOut();
            } finally {
              setSignOutSending(false);
            }
            if (!sent) {
              // Still signed in; the dialog offers "Stay" or "Sign out anyway".
              setSignOutHeld(true);
              return 'held';
            }
          }
          setSignOutHeld(false);
          // I4: the mark goes down first, so a tab closed between the sweep and
          // the logout request still leaves the next load to end the session.
          markLogoutPending();
          // The signed-in person's browser data (unsaved answers) goes next, so
          // a sign-out that fails half-way still leaves none for the next person.
          // The answer queues stop before the sweep: the navigation below fires
          // pagehide, which would otherwise stash their answers again (RW3).
          signOutUserScopedStorage();
          // When the server cannot be reached (offline "Sign out anyway"), the
          // logout stays pending (D1): the next load ends it before any refresh.
          await strategyRef.current.logout();
          set({ status: 'unauthenticated', user: null });
          // Leave for a PUBLIC page, and do it with a full navigation.
          //
          // Two reasons, both load-bearing. Staying put would leave the user on a
          // protected route with status `unauthenticated`, and ProtectedRoute
          // would immediately hand them back to the IdP — signing out would be
          // impossible. And a hard navigation drops the TanStack Query cache,
          // so the next person at this browser cannot read the last one's data
          // out of memory.
          clearSignInAttempt();
          window.location.assign('/opportunities');
          return 'done';
        })();
        const settled = run.then(
          (outcome) => {
            if (outcome === 'held') inFlightRef.current = null;
          },
          (error: unknown) => {
            inFlightRef.current = null;
            throw error;
          },
        );
        inFlightRef.current = settled;
        return settled;
      },
    }),
    [status, user, mode, set, signOutHeld, signOutSending],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
